package com.bulut.mecitogullari.data.repository

import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.Project
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

interface UnitRepository {
    val units: Flow<List<ApartmentUnit>>
    fun unitsFor(projectId: String): Flow<List<ApartmentUnit>>
    /** Creates the block × floor × apartment grid for a new project. */
    suspend fun generateForProject(project: Project)
    suspend fun updateUnit(unit: ApartmentUnit)
    suspend fun deleteForProject(projectId: String)
}

class FirestoreUnitRepository : UnitRepository {

    override val units: Flow<List<ApartmentUnit>> = userCollectionFlow(COLLECTION)

    override fun unitsFor(projectId: String): Flow<List<ApartmentUnit>> =
        units.map { list -> list.filter { it.projectId == projectId } }

    override suspend fun generateForProject(project: Project) {
        val uid = currentUid() ?: return
        val collection = userCollection(uid, COLLECTION)
        // Numbering restarts in every block: A1..A24, B1..B24…
        val generated = buildList {
            project.effectiveBlocks.forEach { block ->
                var number = 1
                for (floor in 1..block.floors) {
                    for (index in 1..block.apartmentsPerFloor) {
                        add(
                            ApartmentUnit(
                                id = newId(),
                                projectId = project.id,
                                block = block.name,
                                floor = floor,
                                indexOnFloor = index,
                                number = number,
                            )
                        )
                        number++
                    }
                }
            }
        }
        // Firestore batches cap at 500 writes.
        generated.chunked(400).forEach { chunk ->
            val batch = Firebase.firestore.batch()
            chunk.forEach { unit -> batch.set(collection.document(unit.id), unit) }
            batch.commit()
        }
    }

    override suspend fun updateUnit(unit: ApartmentUnit) {
        val uid = currentUid() ?: return
        userCollection(uid, COLLECTION).document(unit.id).set(unit)
    }

    override suspend fun deleteForProject(projectId: String) {
        val uid = currentUid() ?: return
        val snapshot = userCollection(uid, COLLECTION).get()
        val toDelete = snapshot.documents.filter {
            runCatching { it.data<ApartmentUnit>().projectId }.getOrNull() == projectId
        }
        toDelete.chunked(400).forEach { chunk ->
            val batch = Firebase.firestore.batch()
            chunk.forEach { doc -> batch.delete(doc.reference) }
            batch.commit()
        }
    }

    private companion object {
        const val COLLECTION = "units"
    }
}
